import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// components
import FormInput from "core/FormInput";
import Add from "core/ReusableIcons/Add";
import Close from "core/ReusableIcons/Close";
import Minus from "core/ReusableIcons/Minus";
import Button from "core/Button";
import SrOnly from "core/SrOnly";
import colors from "core/colors";
import Tooltip from "core/Tooltip";

// helpers
import { getCsrfTokenFromCookie } from "helpers/csrfToken";
import {
    convertCentsToDollars,
    roundToHundredth
} from "frontend/components/helpers";

// data
import {
    CodeContainer,
    AddCodeText,
    MinusContainer,
    PromoCodeSubmitForm,
    SuccessContainer,
    PromotionAppliedText,
    SavingsText,
    ButtonWrapper,
    PromoContainer
} from "./styles";
import { toolTipMsg } from "subscriptions/data";

import {
    MONTHLY_PRICE_PLAN,
    ANNUAL_PRICE_AS_YEARLY,
    CENTS,
    PERCENT
} from "core/constants";
import {
    usePromoCode,
    useSubscriptionPrices
} from "frontend/context/CheckoutContext";

const NO_DISCOUNT = {
    discountType: "",
    amountOff: 0
};

const PromoCode = ({
    numSeats // # of people on team
}) => {
    const [promoCodeValue, setPromoCodeValue] = useState("");
    const {
        setPromoCodeValue: applyPromoCodeValue,
        setPromoCodeDetails,
        promoCodeDetails
    } = usePromoCode();
    const {
        subscriptionPrices,
        setSubscriptionPrices
    } = useSubscriptionPrices();

    const [showInputForCode, setShowInputForCode] = useState(false);
    // Setting it at null to be neutral on load
    // if true/false it would default to error or success state
    const [isPromoValid, setIsPromoValid] = useState(null);
    const [promoApplied, setPromoApplied] = useState(false);
    const [hasSentRequest, setHasSentRequest] = useState(false);

    const sendRequest = async promoCode => {
        setHasSentRequest(true);

        const routeURL = `/coupon/${promoCode}`;

        let response = await fetch(routeURL, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": getCsrfTokenFromCookie()
            }
        });

        // we use the server code to determine valid codes
        // so we can toggle error states in FormInput.
        // using true/ false causes too early validation issues
        if (!response.ok) {
            setIsPromoValid(404);
        } else {
            setIsPromoValid(200);
            response = await response.json();
            return response;
        }
    };

    const handlePromoCodeSubmit = async e => {
        e.preventDefault();
        await applyPromoCode(promoCodeValue);
    };

    const applyPromoCode = async promoCode => {
        const response = await sendRequest(promoCode);

        if (!response) return;

        // apply promo code now that we know it's valid
        applyPromoCodeValue(promoCode);
        setPromoApplied(true);

        // Promo codes can either be % off or $ off
        if (response.coupon.percent_off) {
            const percentOff = response.coupon.percent_off / 100;
            setPromoCodeDetails({
                discountType: PERCENT,
                amountOff: percentOff
            });
        } else if (response.coupon.amount_off) {
            // all dollar amounts come back in cents from the server
            const centsOff = convertCentsToDollars({
                cents: response.coupon.amount_off
            });
            setPromoCodeDetails({ discountType: CENTS, amountOff: centsOff });
        }
    };

    const resetPromoState = () => {
        setPromoApplied(false);
        setShowInputForCode(false);
        setIsPromoValid(null);
        setPromoCodeDetails(NO_DISCOUNT);
    };

    const handleRemoveClick = () => {
        resetPromoState();
        applyPromoCodeValue("");
    };

    const calculateTotals = ({ discountType, amountOff } = {}) => {
        const monthlyNum = parseFloat(subscriptionPrices.monthly);
        const yearlyNum = parseFloat(subscriptionPrices.yearly);

        let yearly, monthly;
        switch (discountType) {
            case CENTS:
                yearly = yearlyNum - amountOff;
                monthly = monthlyNum - amountOff;
                break;
            case PERCENT:
                yearly = yearlyNum - yearlyNum * amountOff;
                monthly = monthlyNum - monthlyNum * amountOff;
                break;
            default:
                yearly = ANNUAL_PRICE_AS_YEARLY * parseInt(numSeats);
                monthly = MONTHLY_PRICE_PLAN * parseInt(numSeats);
        }

        setSubscriptionPrices({ yearly, monthly });
    };

    const onChange = value => {
        // if code is invalid and user alters value
        if (hasSentRequest) {
            setHasSentRequest(false); // reset to has not submitted code
            setIsPromoValid(null); // reset to null
        }
        setPromoCodeValue(value);
    };

    // If URL contains promo code, set the initial promo code value
    useEffect(() => {
        if (!promoCodeValue) {
            const params = new URLSearchParams(window.location.search);
            const COUPON_PARAM = "coupon";
            if (params.has(COUPON_PARAM)) {
                const promoValue = params.get(COUPON_PARAM);
                applyPromoCode(promoValue);
            }
        }
    }, []);

    // If discount applied, update totals
    useEffect(() => {
        calculateTotals(promoCodeDetails);
    }, [promoCodeDetails.discountType, promoCodeDetails.amountOff]);

    // if a user clicks the paypal tile, an unmount will trigger
    // and reset the promo code. Promo codes cannot be used with Paypal
    useEffect(() => {
        return () => {
            resetPromoState();
            // Since component is unmounting, resetPromoState() won't trigger calculateTotals();
            // we will need to call calculateTotals() manually.
            calculateTotals(NO_DISCOUNT);
        };
    }, []);

    const AppliedCodeSuccess = () => (
        <SuccessContainer>
            <div>
                <PromotionAppliedText
                    size="md"
                    copyColor={colors.secondary.orca}
                >
                    Applied promo
                </PromotionAppliedText>

                <SavingsText
                    size="sm"
                    copyColor={colors.accent.lightMode.persian}
                >
                    {promoCodeDetails.discountType === CENTS
                        ? `Saving $${roundToHundredth({
                              num: promoCodeDetails.amountOff
                          })}`
                        : `Saving ${Math.ceil(
                              promoCodeDetails.amountOff * 100
                          )}%`}
                </SavingsText>
            </div>
            <ButtonWrapper onClick={() => handleRemoveClick()} type="button">
                <Tooltip size="small" message={toolTipMsg}>
                    <Close size="sm" />
                </Tooltip>
            </ButtonWrapper>
        </SuccessContainer>
    );

    // 200 or null no errors, 404 has errors
    const errorMessage =
        isPromoValid === 404 ? "This promo code is invalid" : "";

    const InitialAddCode = () => (
        <PromoCodeSubmitForm>
            <MinusContainer
                onClick={() => setShowInputForCode(!showInputForCode)}
                type="button"
            >
                <Minus fillColor={colors.secondary.oolong} />
                <SrOnly>Close Promo Code information form field</SrOnly>
            </MinusContainer>

            <FormInput
                placeholderText="Promo Code"
                labelText="Promo Code"
                size="md"
                value={promoCodeValue}
                onChange={onChange}
                errorMessageOverride={errorMessage}
            />
            <Button size="md" onClick={handlePromoCodeSubmit} type="button">
                Apply
            </Button>
        </PromoCodeSubmitForm>
    );

    const ShowInputField = () => (
        <ButtonWrapper
            onClick={() => setShowInputForCode(!showInputForCode)}
            type="button"
        >
            <PromoContainer>
                <Add size="sm" fillColor={colors.secondary.oolong} />
                <SrOnly>Open Promo Code information form field</SrOnly>
                <AddCodeText size="lg">Add promo code</AddCodeText>
            </PromoContainer>
        </ButtonWrapper>
    );

    if (promoApplied) {
        return (
            <CodeContainer bottomSpacing={true}>
                {AppliedCodeSuccess()}
            </CodeContainer>
        );
    } else if (showInputForCode) {
        return <CodeContainer>{InitialAddCode()}</CodeContainer>;
    } else {
        return (
            <CodeContainer bottomSpacing={true}>
                {ShowInputField()}
            </CodeContainer>
        );
    }
};

PromoCode.propTypes = {
    numSeats: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default PromoCode;
